'use client'
import { FieldContext, FormContext } from 'lib/formContext'
import { type ReactNode, use, useId, useMemo } from 'react'
import { twMerge } from 'tailwind-merge'
import { FormObjectContext } from './FormObject'
import { HelperText } from './HelperText'
import { Label } from './Label'

interface Props {
  name: string
  label: string | null
  children: ReactNode
  info?: ReactNode
  error?: ReactNode
  warning?: ReactNode
  tooltip?: ReactNode
  disabled?: boolean
  className?: string
  grow?: boolean
  required?: boolean | string
}

export function Field({
  name: fieldName,
  label,
  info,
  tooltip,
  children,
  error,
  warning,
  disabled,
  className,
  grow = true,
  required,
}: Props) {
  const objectName = use(FormObjectContext)
  const formContext = use(FormContext)
  const id = useId()
  const name = objectName ? `${objectName}[${fieldName}]` : fieldName
  const isRequired = Boolean(required)
  const validationMessage = Object.entries(formContext?.validity ?? {})
    .filter(([key]) => key === name || key.startsWith(`${name}[`))
    .map(([, message]) => message)
    .join('\n')
  const helperMessage = validationMessage || error || warning || info

  const context = useMemo(
    () => ({
      id,
      name,
      error: Boolean(validationMessage) || Boolean(error),
      warning: Boolean(warning),
      disabled: Boolean(disabled),
      onChange: (value: unknown) => {
        formContext?.onChange?.(name, value)
      },
      grow,
      required: isRequired,
    }),
    [
      id,
      name,
      error,
      warning,
      disabled,
      validationMessage,
      grow,
      isRequired,
      formContext,
    ],
  )

  return (
    <FieldContext.Provider value={context}>
      {objectName && (
        <input type="hidden" name={objectName} value={fieldName} />
      )}
      <div className={twMerge('group flex grow flex-col gap-2', className)}>
        {label && (
          <Label htmlFor={id} tooltip={tooltip}>
            {label}
          </Label>
        )}
        <FieldContext.Provider value={context}>
          {children}
        </FieldContext.Provider>
        <HelperText className="min-h-4">{helperMessage}</HelperText>
      </div>
    </FieldContext.Provider>
  )
}
